// ExportSapHistoric.tsx
import {
  Button,
  Flex,
  Text,
  Spinner,
  Popover,
  PopoverTrigger,
  PopoverArrow,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  Input,
  PopoverFooter,
  Spacer,
  Box
} from '@chakra-ui/react'
import { addDays, format, parseISO, subDays } from 'date-fns'
import { FC, useCallback, useMemo, useRef, useState } from 'react'
import CsvDownloader from 'react-csv-downloader'
import { decodeToken } from 'react-jwt'

import suppliers from '@helper/suppliers'
import mrp from '@service/mrp'

import sapHistoricHeader from '@base/variables/headers/suppliers/saphistoric'

interface ExportSapHistoricProps {
  code: string
  days: number
}

const ExportSapHistoric: FC<ExportSapHistoricProps> = ({ code, days }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  // Datas locais (toISOString usa UTC e muda o dia após as 21h no Brasil)
  const today = format(new Date(), 'yyyy-MM-dd')
  const oneMonthAgo = format(subDays(new Date(), days), 'yyyy-MM-dd')
  const [startDate, setStartDate] = useState<string>(oneMonthAgo)
  const [endDate, setEndDate] = useState<string>(today)
  const [data, setDataNew] = useState<any[]>([])

  // Lido uma vez: recriado a cada render invalidava os useCallback que dependem dele
  const decodedToken = useMemo(() => {
    const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID
    const audience = process.env.REACT_APP_AUTH0_AUDIENCE
    const userInfos = JSON.parse(
      localStorage.getItem(
        `@@auth0spajs@@::${clientId}::${audience}::openid profile email offline_access`
      )
    )
    return decodeToken(userInfos?.body.access_token) as any
  }, [])

  const headers = useMemo(() => sapHistoricHeader, [])
  const columns = useMemo(() => {
    return (
      headers.flatMap(header =>
        header.columns
          ?.filter(column => column.show)
          .map(column => ({
            id: column.id,
            displayName: `${column.name}`
          }))
      ) || []
    )
  }, [headers])
  function addOneDay(dateString: string): string {
    return format(addDays(parseISO(dateString), 1), 'yyyy-MM-dd')
  }
  const csvDownload = useRef<CsvDownloader>(null)

  const rows = useMemo(() => {
    const parsedData = suppliers.parseResultHistoric(data)

    const body = []

    for (const row of parsedData) {
      const item: any = {}

      for (const column of columns) {
        if (column.id === 'parametros-emax') {
          if (row[column.id].includes('.')) {
            item[column.id] = Number(row[column.id].replace('.', ''))
          } else {
            item[column.id] = Number(row[column.id])
          }
        } else {
          item[column.id] = row[column.id]
        }
      }

      body.push(item)
    }

    return body
  }, [columns, data])

  const handleExport = useCallback(async () => {
    setIsLoading(true)
    const idUser =
      decodedToken?.role === 'Administrador' ? null : decodedToken?.userid
    try {
      const {
        data: { data }
      } = await mrp.getHistoricSapExport(
        code,
        startDate,
        addOneDay(endDate),
        idUser
      )
      setDataNew(data)
      if (csvDownload.current) {
        csvDownload.current.handleClick()
      }
    } catch {
      // Falha no download: mantém a tela; o botão volta ao estado normal
    } finally {
      setIsLoading(false)
    }
  }, [code, endDate, startDate, decodedToken])

  return (
    <Box>
      <Popover>
        <PopoverTrigger>
          <Button
            ms="auto"
            alignItems="center"
            justifyContent="center"
            bg="gray.200"
            w="87px"
            h="30px"
            mt={2}
            cursor="pointer"
            lineHeight="100%"
            borderRadius="10px"
            _hover={{ bg: 'gray.200' }}
          >
            <>
              <Text
                size="xs"
                fontSize="12px"
                letterSpacing=".2px"
                p="4px 6px"
                bg="gray.200"
                fontWeight="400"
                _hover={{ bg: 'gray.200' }}
                color="black"
              >
                {isLoading ? <Spinner size="sm" color="white" /> : 'Exportar'}
              </Text>
            </>
          </Button>
        </PopoverTrigger>
        <PopoverContent bg={'white'}>
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader>Exportar!</PopoverHeader>
          <PopoverBody>
            Selecione o período desejado
            <Flex
              flex={1}
              mr={{ base: 0, md: 4 }}
              mb={{ base: 4, md: 0 }}
              mt={2}
            >
              <Text fontWeight={700}> Início: {'\u00a0'}</Text>
              {'\u00a0'}
              {'\u00a0'}
              <Input
                type="date"
                bg="white"
                value={startDate}
                borderColor="black"
                onChange={e => setStartDate(e.target.value)}
              />
            </Flex>
            <Flex flex={1} mr={{ base: 0, md: 4 }} mb={{ base: 4, md: 0 }}>
              <Text fontWeight={700}>
                Fim: {'\u00a0'}
                {'\u00a0'}
              </Text>
              {'\u00a0'}
              {'\u00a0'}
              {'\u00a0'}
              {'\u00a0'}
              {'\u00a0'}
              <Input
                type="date"
                bg="white"
                value={endDate}
                borderColor="black"
                onChange={e => setEndDate(e.target.value)}
              />
            </Flex>
          </PopoverBody>
          <PopoverFooter
            border="0"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            pb={4}
          >
            <Spacer />
            <Button variant="ghost" color="black" onClick={handleExport}>
              Download
            </Button>
          </PopoverFooter>
        </PopoverContent>
      </Popover>
      <CsvDownloader
        filename={'Mrp-PedidosSap-' + code}
        columns={columns}
        datas={rows}
        ref={csvDownload}
        text="."
        color="white"
        separator=";"
        wrapColumnChar='"'
        bom={true}
        style={{ display: 'none' }}
      ></CsvDownloader>
    </Box>
  )
}

export default ExportSapHistoric
